<template>
  <section :class="{ disabledSelect: disableDropDown || completed || showOptions }">
    <label v-if="label" v-text="label" :id="dropdownId"> </label>
    <button
      :aria-controls="isDropdown ? 'dropdownmenu' : null"
      :aria-haspopup="isDropdown ? true : null"
      :aria-labelledby="isDropdown ? dropdownId : null"
      :class="[{ alert: alert }, 'select', 'focusable']"
      :role="isDropdown ? null : 'textbox'"
      @click="openDropDown"
      @keyup.down="openDropDown"
      @keyup.space="openDropDown"
      @keyup.up="openDropDown"
      id="select"
      tabindex="0"
    >
      <div id="input">
        <blinking-cursor-text
          v-if="blinkingText"
          :text="blinkingText"
          :text-step="stepIndex"
        ></blinking-cursor-text>
      </div>
      <div class="arrow">
        <div></div>
      </div>
    </button>
    <ul aria-label="Dropdown Options" class="options-container" id="dropdownmenu" role="menu">
      <li
        v-for="(option, index) in displayOptions"
        @blur="handleBlur"
        @focus="e => (focusedOption = e.target)"
        @click="selectOption(option)"
        @keyup.down="shiftOptionFocus(displayOptions.length - 1 === index ? index : index + 1)"
        @keyup.enter="selectOption(option)"
        @keyup.up="shiftOptionFocus(index ? index - 1 : index)"
        :data-index="index"
        :key="index"
        :class="`option focusable dropdown-${dropdownId}-option`"
        role="menuitem"
        tabindex="0"
      >
        <p v-text="option.text"></p>
      </li>
    </ul>
  </section>
</template>

<script>
import TweenMax from 'gsap';
import BlinkingCursorText from './BlinkingCursorText.vue';
import CommonAudio from '../../mixins/CommonAudio';

export default {
  props: {
    options: {
      type: Array,
      default: () => [],
      required: true
    },
    dropdownId: {
      type: String,
      default: '',
      required: true
    },
    completed: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    selectedCorrect: {
      type: Function,
      default: () => {}
    },
    selectedIncorrect: {
      type: Function,
      default: () => {}
    },
    stepIndex: {
      type: Number,
      required: true
    },
    disableDropDown: {
      type: Boolean,
      default: false
    },
    showOptions: {
      type: Boolean,
      default: false
    }
  },

  components: {
    BlinkingCursorText
  },

  mixins: [CommonAudio],

  data() {
    return {
      alert: false,
      blinkingText: false,
      collapsed: false
    };
  },
  computed: {
    displayOptions() {
      return this.collapsed ? this.options : [];
    },
    isDropdown() {
      return this.collapsed || !this.blinkingText;
    }
  },
  methods: {
    handleBlur(e) {
      setTimeout(() => {
        if (e.target.getAttribute('data-index') === this.focusedOption.getAttribute('data-index')) {
          this.collapsed = false;
        }
      }, 250);
    },
    openDropDown() {
      if (this.disableDropDown || this.completed || this.blinkingText || this.showOptions) return;
      this.collapsed = !this.collapsed;

      setTimeout(() => {
        const optionNodes = document.getElementsByClassName(`dropdown-${this.dropdownId}-option`);
        optionNodes.item(0).focus();
      }, 250);
    },
    selectOption(option) {
      if (option.isValid) {
        this.blinkingText = option.text;
        this.collapsed = false;
        this.alert = false;
        this.selectedCorrect(this.stepIndex);
        return this.sndBell();
      }
      this.alert = true;
      TweenMax.delayedCall(0.6, () => {
        this.alert = false;
      });
      this.selectedIncorrect();
      this.sndError();
      return false;
    },
    shiftOptionFocus(index) {
      const optionNodes = document.getElementsByClassName(`dropdown-${this.dropdownId}-option`);
      optionNodes.item(index).focus();
    }
  }
};
</script>

<style lang="scss" scoped>
$disabled-color: lighten(gray, 20%);

section {
  margin: 2% 0;
  .select {
    display: block;
    border: 2px solid black;
    cursor: pointer;
    background: white;
    height: 34px;
    padding: 0px;
    width: 100%;

    > #input {
      display: inline-block;
      overflow-x: auto;
      width: calc(100% - 36px);

      p {
        font-size: 16px;
        text-align: left;
      }
    }
    &.alert {
      background-color: red;
      .arrow {
        background-color: white;
      }
    }
    p {
      display: inline-block;
      font-size: 0.92rem;
      margin: 5px 3% 0px 3%;
      white-space: nowrap;
      width: calc(100% - 36px);

      span {
        color: red;
      }
    }
    .arrow {
      float: right;
      border-left: 1px solid #000;
      width: 35px;
      height: 30px;
      cursor: pointer;
      > div {
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 10px solid #000;
        margin-top: 9px;
        margin-left: 11px;
      }
    }
  }
  .options-container {
    background-color: #C4C4C4;
    border: none;
    cursor: pointer;
    margin: 0px;
    padding: 0px;
    position: absolute;
    width: 578px;
    z-index: 1;

    .option {
      border: none;
      list-style: none;
      margin: 0px;
      padding: 0px;

      &:hover {
        background-color: black;
        transition-duration: 0.5s;
      }
      p {
        color: black;
        &:hover {
          color: white;
        }
      }
    }
  }
  .disabledSelect {
    color: $disabled-color;
    .select {
      border-color: $disabled-color;
      color: $disabled-color;
      cursor: default;
      .arrow {
        border-left: 1px solid $disabled-color;
        > div {
          border-top: 10px solid $disabled-color;
        }
      }
    }
  }
  label {
    display: block;
    margin: 1% 0;
  }
}
</style>
